import React from "react";
import { PositionSuggestion } from "../types/position";
import "./PositionAdjustmentSuggestions.css";

interface PositionAdjustmentSuggestionsProps {
  suggestions: PositionSuggestion[];
  onApplySuggestion: (suggestion: PositionSuggestion) => void;
  className?: string;
}

const PositionAdjustmentSuggestions: React.FC<
  PositionAdjustmentSuggestionsProps
> = ({ suggestions, onApplySuggestion, className = "" }) => {
  if (suggestions.length === 0) return null;

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case "low":
        return "#10b981";
      case "medium":
        return "#f59e0b";
      case "high":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getImpactLabel = (impact: string) => {
    switch (impact) {
      case "low":
        return "Low Impact";
      case "medium":
        return "Medium Impact";
      case "high":
        return "High Impact";
      default:
        return "Unknown Impact";
    }
  };

  const formatValue = (value: any, type: string) => {
    if (type === "quantity") {
      return value.toLocaleString();
    } else if (type === "price") {
      return `$${value.toLocaleString()}`;
    }
    return value;
  };

  return (
    <div className={`position-adjustment-suggestions ${className}`}>
      <div className="suggestions-header">
        <span className="suggestions-icon">💡</span>
        <span className="suggestions-title">
          Position Adjustment Suggestions
        </span>
      </div>

      <div className="suggestions-list">
        {suggestions.map((suggestion) => (
          <div key={suggestion.id} className="suggestion-item">
            <div className="suggestion-header">
              <div className="suggestion-type">
                {suggestion.type.charAt(0).toUpperCase() +
                  suggestion.type.slice(1)}{" "}
                Adjustment
              </div>
              <div
                className="impact-badge"
                style={{ backgroundColor: getImpactColor(suggestion.impact) }}
              >
                {getImpactLabel(suggestion.impact)}
              </div>
            </div>

            <div className="suggestion-content">
              <div className="value-comparison">
                <div className="current-value">
                  <span className="value-label">Current:</span>
                  <span className="value">
                    {formatValue(suggestion.currentValue, suggestion.type)}
                  </span>
                </div>
                <div className="arrow">→</div>
                <div className="suggested-value">
                  <span className="value-label">Suggested:</span>
                  <span className="value">
                    {formatValue(suggestion.suggestedValue, suggestion.type)}
                  </span>
                </div>
              </div>

              <div className="suggestion-reason">{suggestion.reason}</div>
            </div>

            <div className="suggestion-actions">
              <button
                onClick={() => onApplySuggestion(suggestion)}
                className="apply-suggestion-button"
              >
                Apply Suggestion
              </button>

              <button className="ignore-suggestion-button">Ignore</button>
            </div>
          </div>
        ))}
      </div>

      <div className="suggestions-footer">
        <p className="suggestions-note">
          These suggestions are based on validation rules and best practices.
          Review carefully before applying.
        </p>
      </div>
    </div>
  );
};

export default PositionAdjustmentSuggestions;
